import React, { useEffect, useRef, useState } from 'react';
import './Experience.css';

const experiences = [
  {
    title: "Framework Engineer (Data Engineer)",
    company: "Quantiphi",
    location: "Trivandrum",
    duration: "Mar 2025 - May 2025",
    type: "Current Role",
    description: [
      "Designing and maintaining automated ETL pipelines using Apache Airflow, improving data reliability and streamlining workflow orchestration",
      "Utilizing AWS Glue for data extraction, transformation, and loading tasks with schema inference and dynamic frame handling",
      "Performing data cleaning and validation operations on large datasets in cloud-based environments"
    ],
    technologies: ["Apache Airflow", "AWS Glue", "Amazon S3", "Redshift", "Python"],
    icon: "🏗️",
    gradient: "blue"
  },
  {
    title: "Frontend Developer Intern",
    company: "Stockbee",
    duration: "Dec 2024 - Feb 2025",
    type: "Recent",
    description: [
      "Developed responsive user interfaces using React.js with focus on accessibility and seamless UX",
      "Implemented Redux for efficient state management and integrated session management for enhanced security",
      "Collaborated on full-stack features improving overall application performance"
    ],
    technologies: ["React.js", "Redux", "JavaScript", "CSS", "Session Management"],
    icon: "⚛️",
    gradient: "purple"
  },
  {
    title: "Full-stack Developer Intern",
    company: "AppXcess Technologies",
    duration: "Sep 2024 - Oct 2024",
    type: "Recent",
    description: [
      "Developed and integrated OpenAI APIs for automated content generation enhancing platform capabilities",
      "Implemented social media integration features for seamless content sharing across multiple platforms",
      "Built full-stack solutions using MERN stack with focus on API handling and dynamic UI components"
    ],
    technologies: ["React.js", "Node.js", "OpenAI API", "Social Media APIs", "MERN Stack"],
    icon: "🚀",
    gradient: "green"
  },
  {
    title: "SDE (Backend Developer Intern)",
    company: "Vibtree Technologies",
    duration: "Oct 2023 - Jan 2024",
    type: "Foundation",
    description: [
      "Developed robust backend systems using Django, significantly enhancing scalability and achieving 30% improvement in response times",
      "Successfully integrated APIs, streamlining data flow and improving efficiency by 30%",
      "Created custom packages and libraries ensuring scalable solutions and improving system performance by 30%"
    ],
    technologies: ["Django", "Python", "REST APIs", "Backend Architecture", "Performance Optimization"],
    icon: "⚙️",
    gradient: "orange"
  },
  {
    title: "MERN Stack Developer",
    company: "Channel B.Tech",
    duration: "July 2023",
    type: "Foundation",
    description: [
      "Collaborated with team members to construct full-stack web applications utilizing the MERN stack",
      "Crafted responsive interfaces using React and optimized database management with MongoDB",
      "Achieved 35% increase in user satisfaction and 45% reduction in data processing time"
    ],
    technologies: ["MongoDB", "Express.js", "React.js", "Node.js", "Database Optimization"],
    icon: "💻",
    gradient: "indigo"
  }
];

function Experience() {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return (
    <section className={`experience-section ${isVisible ? 'visible' : ''}`} ref={ref}>
      <div className="container">
        {/* Header */}
        <div className="section-header">
          {/* <div className="header-icon">💼</div> */}
          <h2 className="section-title">Professional Experience</h2>
          <p className="section-description">
            A journey through innovative companies and cutting-edge technologies
          </p>
        </div>

        {/* Experience List */}
        <div className="experience-list">
          {experiences.map((experience, index) => (
            <div key={index} className={`experience-item ${experience.gradient} fade-in-${index}`}>
              <div className="experience-content">
                {/* Left Side - Icon & Timeline */}
                <div className="experience-left">
                  <div className="icon-circle">
                    <span className="experience-icon">{experience.icon}</span>
                  </div>
                  <div className="connector-line"></div>
                </div>

                {/* Right Side - Content */}
                <div className="experience-right">
                  <div className="experience-header">
                    <div className="title-section">
                      <h3 className="job-title">{experience.title}</h3>
                      <div className="company-info">
                        <span className="company-name">{experience.company}</span>
                        {experience.location && (
                          <span className="location">• {experience.location}</span>
                        )}
                      </div>
                      <span className="duration">{experience.duration}</span>
                    </div>
                    {/* <div className="status-badge">
                      <span className={`badge ${experience.type.toLowerCase().replace(' ', '-')}`}>
                        {experience.type}
                      </span>
                    </div> */}
                  </div>

                  <div className="experience-details">
                    <div className="achievements">
                      {experience.description.map((item, i) => (
                        <div key={i} className="achievement">
                          <span className="bullet">•</span>
                          <p>{item}</p>
                        </div>
                      ))}
                    </div>

                    <div className="technologies">
                      {experience.technologies.map((tech, i) => (
                        <span key={i} className="tech-tag">{tech}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Stats
        <div className="stats-section">
          <div className="stat-item">
            <div className="stat-number">2+</div>
            <div className="stat-label">Years Experience</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">5</div>
            <div className="stat-label">Companies</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">15+</div>
            <div className="stat-label">Technologies</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">10+</div>
            <div className="stat-label">Projects</div>
          </div>
        </div> */}
      </div>
    </section>
  );
}

export default Experience;
